// Piezas comunes del módulo Marketing: formatos, etiquetas y estados.
// Solo tokens semánticos (claro/oscuro); los colores de canal salen de
// `orion-tone tone-*` del shell.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2, PlugZap, RefreshCw } from 'lucide-react';

import { Button, EmptyState, ErrorNote } from '@/modules/orion-shell/ui';

import { MarketingNoDisponible, setDemo } from './api';

// Día civil de la org. El backend manda fechas YYYY-MM-DD ya en día civil;
// aquí solo se calcula «hoy» y el inicio de mes para pedir el periodo.
export const TZ = 'America/Mexico_City';

export function hoyCivil(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
}

export function sumarDias(fecha: string, n: number): string {
  const d = new Date(`${fecha}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function fechaCorta(fecha: string): string {
  return new Date(`${fecha}T12:00:00Z`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function mesLargo(mes: string): string {
  const s = new Date(`${mes}-15T12:00:00Z`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const nf = new Intl.NumberFormat('es-MX');
export const num = (n: number | null | undefined) => (n == null ? '—' : nf.format(n));

/** Centavos → «$1,234» (o con centavos si es menor a $100). Null → «—». */
export function dinero(cents: number | null | undefined, moneda = 'MXN'): string {
  if (cents == null) return '—';
  const v = cents / 100;
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: moneda,
    maximumFractionDigits: Math.abs(v) < 100 ? 2 : 0,
    minimumFractionDigits: 0,
  }).format(v);
}

export const CANAL: Record<string, { label: string; tone: 'violet' | 'indigo' | 'blue' | 'magenta' | 'purple' }> = {
  meta: { label: 'Meta (Facebook e Instagram)', tone: 'blue' },
  google: { label: 'Google Ads', tone: 'indigo' },
  organico: { label: 'Orgánico', tone: 'violet' },
  whatsapp: { label: 'WhatsApp', tone: 'purple' },
  correo: { label: 'Correo', tone: 'magenta' },
  prensa: { label: 'Prensa', tone: 'violet' },
  directo: { label: 'Directo', tone: 'indigo' },
};
export const canalLabel = (c: string) => CANAL[c]?.label ?? c;
export const canalTone = (c: string) => CANAL[c]?.tone ?? 'violet';

export const ESTADO: Record<string, string> = {
  active: 'Activa',
  paused: 'En pausa',
  draft: 'Borrador',
  completed: 'Terminada',
  error: 'Con error',
  pending: 'Pendiente',
};

export function DemoChip() {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
      Demostración
    </span>
  );
}

/** Estados de carga / error compartidos por las 3 pestañas. */
export function EstadoCarga({
  loading,
  error,
  onRetry,
  onDemo,
  children,
}: {
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  onDemo: () => void;
  children: ReactNode;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground" role="status">
        <Loader2 size={16} className="animate-spin" aria-hidden /> Cargando…
      </div>
    );
  }
  if (error instanceof MarketingNoDisponible) {
    return (
      <div className="py-6">
        <EmptyState
          icon={<PlugZap size={18} />}
          title="El tablero de Marketing todavía no recibe datos"
          action={
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              <Button onClick={onRetry}>
                <RefreshCw size={14} aria-hidden /> Reintentar
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setDemo(true);
                  onDemo();
                }}
              >
                Ver con datos de demostración
              </Button>
            </div>
          }
        >
          El servidor aún no tiene esta sección. En cuanto se publique, aquí verás el gasto, los resultados y el
          presupuesto reales de tu organización.
        </EmptyState>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-3 py-4">
        <ErrorNote>{error instanceof Error ? error.message : 'No se pudo cargar.'}</ErrorNote>
        <Button onClick={onRetry}>
          <RefreshCw size={14} aria-hidden /> Reintentar
        </Button>
      </div>
    );
  }
  return <>{children}</>;
}

/** Mide el ancho disponible para recharts (sin scroll horizontal en 390 px). */
export function ChartBox({ children, height = 224 }: { children: (width: number) => ReactNode; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setW(Math.floor(el.getBoundingClientRect().width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="w-full min-w-0" style={{ height }}>
      {w > 0 && children(w)}
    </div>
  );
}

/** Tarjeta de indicador con valor ya formateado (dinero, %, etc.). */
export function Indicador({
  icon,
  label,
  value,
  sub,
  tone = 'violet',
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sub?: ReactNode;
  tone?: 'violet' | 'indigo' | 'blue' | 'magenta' | 'purple';
}) {
  return (
    <div className="orion-card min-w-0 rounded-[20px] border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center gap-2 sm:gap-3">
        <span className={`orion-tone tone-${tone} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl`} aria-hidden>
          {icon}
        </span>
        <span className="min-w-0 flex-1 text-sm font-semibold leading-tight text-foreground">{label}</span>
      </div>
      <div className="mt-3 truncate text-[24px] font-semibold leading-none tracking-tight text-foreground tabular-nums sm:text-[28px]">
        {value}
      </div>
      {sub && <div className="mt-2 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
